import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { AppCheckboxFormLabel, AppForm, AppTypography } from '@/components/atoms';
import { ALL_PRINT_COL_VALUES, PRINT_COL_OPTIONS } from '@/constants/consentForm';
import type { ConsentFormData } from '@/schemas/consentSchema';
import type { LabelChecks, SetLabelChecks } from '../types';
import { ensureArray } from '../utils';

interface PrintOptionsSectionProps {
  allLabelKeys: string[];
  control: Control<ConsentFormData>;
  labelChecks: LabelChecks;
  patientAddress: string;
  patientBirthOfDate: string;
  patientName: string;
  setLabelChecks: SetLabelChecks;
}

export const PrintOptionsSection: React.FC<PrintOptionsSectionProps> = ({
  allLabelKeys,
  control,
  labelChecks,
  patientAddress,
  patientBirthOfDate,
  patientName,
  setLabelChecks,
}) => {
  return (
    <div>
      <div className="flex flex-col gap-2">
        <Controller
          name="print_cols"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const value = ensureArray(field.value);
            const allChecked = allLabelKeys.every(k => !!labelChecks[k]);
            const anyChecked = allLabelKeys.some(k => !!labelChecks[k]);
            const isIndeterminate = anyChecked && !allChecked;

            return (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <AppCheckboxFormLabel
                    label="チェックした項目は同意書に印字する(医師名印字の場合、医師の捺印必要)"
                    tabIndex={-1}
                    checked={allChecked}
                    indeterminate={isIndeterminate}
                    onChange={e => {
                      const checked = e.target.checked;
                      // "Check all" affects all print toggles on this screen (not only the 3 patient fields).
                      // Persist only patient info print flags via print_cols.
                      field.onChange(checked ? ALL_PRINT_COL_VALUES : []);
                      setLabelChecks(prev => {
                        const next = { ...prev };
                        allLabelKeys.forEach(k => {
                          next[k] = checked;
                        });
                        return next;
                      });
                    }}
                  />
                </div>
                <div className="flex flex-col gap-2 mt-2">
                  <AppForm.Item validateStatus={error ? 'error' : undefined} help={error?.message}>
                    <div className="flex flex-col items-start gap-2">
                      {PRINT_COL_OPTIONS.map(option => {
                        const displayText =
                          option.value === 'patient_address'
                            ? patientAddress
                            : option.value === 'patient_name'
                              ? patientName
                              : patientBirthOfDate;
                        const rowChecked = value?.includes(option.value);
                        const toggleRow = (checked: boolean) => {
                          const next = checked
                            ? [...value, option.value]
                            : value.filter((v: string) => v !== option.value);
                          field.onChange(next);
                          setLabelChecks(prev => ({ ...prev, [option.value]: checked }));
                        };
                        /* create/update spec 共通: 印字 checkbox + 項目 Label + 値。formMode は page から渡し、将来の編集専用 UI 分岐用 */
                        const printColInputId = `consent-print-col-${option.value}`;
                        return (
                          <div
                            key={option.value}
                            className="grid grid-cols-[minmax(120px,164px)_1fr] gap-1 items-start"
                          >
                            <AppCheckboxFormLabel
                              id={printColInputId}
                              label={option.label}
                              tabIndex={-1}
                              checked={rowChecked}
                              onChange={e => toggleRow(e.target.checked)}
                            />
                            <label
                              htmlFor={printColInputId}
                              className="min-w-0 text-left cursor-pointer"
                            >
                              <AppTypography.Text className="text-gray-700 break-all">
                                {displayText}
                              </AppTypography.Text>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </AppForm.Item>
                </div>
              </>
            );
          }}
        />
      </div>
    </div>
  );
};
